import PropTypes from 'prop-types';
import { createContext, useState, useEffect } from 'react';
import Axios from 'axios';
import { useSelector } from 'react-redux';

// project import
import useLocalStorage from '@/hooks/useLocalStorage';

// initial state
const initialState = {
  plants: [],
  activePlant: null,
  userType: null,
  loading: true,
  switchPlant: () => {},
  refreshPlants: () => {}
};

// ==============================|| PLANT CONTEXT & PROVIDER ||============================== //

const PlantContext = createContext(initialState);

function PlantProvider({ children }) {
  const [plants, setPlants] = useState([]);
  const [activePlant, setActivePlantState] = useLocalStorage('active-plant', null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Get auth state from Redux
  const isLoggedIn = useSelector((state) => state?.auth?.userModel?.isLogin);

  // Load user plants when user logs in
  useEffect(() => {
    console.log('PlantProvider useEffect triggered, isLoggedIn:', isLoggedIn, 'initialized:', initialized);
    const token = localStorage.getItem('Token');
    console.log('Token exists:', !!token);

    if (isLoggedIn && token) {
      console.log('Calling loadUserPlants...');
      loadUserPlants();
      setInitialized(true);
    } else {
      console.log('Not loading plants - isLoggedIn:', isLoggedIn, 'token:', !!token);
      // If logged out, reset the initialized flag
      if (!isLoggedIn) {
        setInitialized(false);
      }
    }
  }, [isLoggedIn]);

  const loadUserPlants = async () => {
    try {
      console.log('loadUserPlants started');
      setLoading(true);
      const token = localStorage.getItem('Token');

      if (!token) {
        console.error('No authentication token found');
        setLoading(false);
        return;
      }

      console.log('Making API call to /api/plants/user');
      const response = await Axios.get('/bp/plants/user', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('API Response:', response);

      if (response?.data?.status === 'success') {
        const { user_type, plants: userPlants, default_plant } = response?.data?.data;

        setUserType(user_type);
        setPlants(userPlants);

        console.log('Current activePlant from localStorage:', activePlant);
        console.log('User plants from API:', userPlants);

        // Check if activePlant exists and is valid
        const hasValidActivePlant = activePlant && activePlant !== null && typeof activePlant === 'object' && activePlant.id;
        const activePlantInList = hasValidActivePlant && userPlants?.find(p => p.id === activePlant.id);

        if (hasValidActivePlant && activePlantInList) {
          // Keep the saved plant if it exists and user still has access
          console.log('Using saved plant:', activePlant);
        } else if (userPlants && userPlants.length > 0) {
          // Set first plant as default when no valid plant is saved
          const firstPlant = userPlants[0];
          console.log('Setting first plant as default:', firstPlant);
          setActivePlantState(firstPlant);
        } else if (default_plant) {
          // Fallback to default_plant if no plants in list
          console.log('Using default plant:', default_plant);
          setActivePlantState(default_plant);
        }
      } else {
        console.error('Failed to load plants:', response.data.message);
      }
    } catch (error) {
      console.error('Error loading plants:', error);
      if (error.response?.status === 401) {
        console.error('Authentication failed. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const switchPlant = async (plant) => {
    try {
      const token = localStorage.getItem('Token');

      if (!token) {
        console.error('No authentication token found');
        return;
      }

      // Call API to set active plant (includes access control check)
      const response = await Axios.post(
        '/bp/plants/set-active',
        { plant_id: plant.id },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 'success') {
        // Update local state
        setActivePlantState(plant);

        // Reload the page to refresh plant-specific data
        window.location.reload();
      } else {
        console.error('Failed to switch plant:', response.data.message);
        alert('Failed to switch plant: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error switching plant:', error);

      if (error.response?.status === 403) {
        alert('Access denied: ' + error.response.data.message);
      } else if (error.response?.status === 401) {
        alert('Authentication failed. Please login again.');
      } else {
        alert('Failed to switch plant. Please try again.');
      }
    }
  };

  const refreshPlants = () => {
    loadUserPlants();
  };

  return (
    <PlantContext.Provider
      value={{
        plants,
        activePlant,
        userType,
        loading,
        switchPlant,
        refreshPlants
      }}
    >
      {children}
    </PlantContext.Provider>
  );
}

PlantProvider.propTypes = {
  children: PropTypes.node
};

export { PlantProvider, PlantContext };
